// Who may sign in to an application: everyone, or selected user types / groups (docs/access.md).
import { useId } from "react";
import {
  BUILT_IN_USER_TYPES,
  DEFAULT_NEW_CLIENT_ACCESS,
  type ClientAccessDto,
  type GroupDto,
  type UserTypeDto,
} from "@platform/core";
import { Field, MultiSelect, NativeSelect } from "@platform/ui";
import { useT, type MessageKey } from "../../i18n";
import { groupOptions, typeOptions } from "../../lib/access";

export const NEW_CLIENT_ACCESS: ClientAccessDto = { ...DEFAULT_NEW_CLIENT_ACCESS };

type TypeLike = Pick<UserTypeDto, "id" | "name" | "policy">;

/** Validation problem of an access rule (mirrors the API checks), or null. */
export function accessProblem(access: ClientAccessDto, types: readonly TypeLike[]): MessageKey | null {
  if (access.mode === "restricted" && access.userTypes.length + access.groups.length === 0)
    return "access.needOne";
  if (access.signUpUserType) {
    const st = types.find((x) => x.id === access.signUpUserType);
    if (st && !st.policy.selfSignUp) return "access.signUpNotAllowed";
    if (access.mode === "restricted" && !access.userTypes.includes(access.signUpUserType))
      return "access.signUpNotListed";
  }
  return null;
}

/** Drop empty optional fields so the request matches the API schema. */
export function cleanAccess(a: ClientAccessDto): ClientAccessDto {
  return {
    mode: a.mode,
    userTypes: a.mode === "all" ? [] : a.userTypes,
    groups: a.mode === "all" ? [] : a.groups,
    ...(a.signUpUserType ? { signUpUserType: a.signUpUserType } : {}),
  };
}

export function AccessEditor({
  value,
  onChange,
  disabled,
  types,
  groups,
}: {
  value: ClientAccessDto;
  onChange: (v: ClientAccessDto) => void;
  disabled?: boolean;
  /** User types (undefined without settings:read → built-in types). */
  types: readonly UserTypeDto[] | undefined;
  groups: readonly GroupDto[] | undefined;
}) {
  const { t } = useT();
  const name = useId();
  const typeList: readonly TypeLike[] = types ?? BUILT_IN_USER_TYPES;
  const problem = accessProblem(value, typeList);
  const signUpTypes = typeList.filter(
    (x) => x.policy.selfSignUp && (value.mode === "all" || value.userTypes.includes(x.id)),
  );
  const signUpIds = [
    ...new Set([...signUpTypes.map((x) => x.id), ...(value.signUpUserType ? [value.signUpUserType] : [])]),
  ];
  const radio = (mode: ClientAccessDto["mode"], label: MessageKey, hint: MessageKey) => (
    <label className="flex cursor-pointer items-start gap-2 rounded-md py-1">
      <input
        type="radio"
        name={name}
        className="mt-1"
        value={mode}
        checked={value.mode === mode}
        disabled={disabled}
        data-testid={`access-mode-${mode}`}
        onChange={() => onChange({ ...value, mode })}
      />
      <span>
        <span className="block text-sm font-medium">{t(label)}</span>
        <span className="block text-xs text-ink-2">{t(hint)}</span>
      </span>
    </label>
  );
  return (
    <div>
      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium">{t("access.whoCanSignIn")}</legend>
        {radio("all", "access.everyone", "access.everyoneHint")}
        {radio("restricted", "access.restricted", "access.restrictedHint")}
      </fieldset>
      {value.mode === "restricted" && (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Field label={t("access.userTypes")}>
            {(p) => (
              <MultiSelect
                {...p}
                data-testid="access-user-types"
                label={t("access.userTypes")}
                disabled={disabled}
                value={value.userTypes}
                onChange={(v) => onChange({ ...value, userTypes: v })}
                options={typeOptions(typeList)}
                placeholder={t("common.none")}
                searchPlaceholder={t("common.search")}
              />
            )}
          </Field>
          <Field label={t("access.groups")}>
            {(p) => (
              <MultiSelect
                {...p}
                data-testid="access-groups"
                label={t("access.groups")}
                disabled={disabled}
                value={value.groups}
                onChange={(v) => onChange({ ...value, groups: v })}
                options={groupOptions(groups)}
                placeholder={t("common.none")}
                emptyText={t("groups.empty")}
                searchPlaceholder={t("common.search")}
              />
            )}
          </Field>
        </div>
      )}
      <Field label={t("access.signUpAs")} hint={t("access.signUpAsHint")}>
        {(p) => (
          <NativeSelect
            {...p}
            data-testid="access-sign-up"
            disabled={disabled}
            className="max-w-sm"
            value={value.signUpUserType ?? ""}
            onChange={(e) => {
              const { signUpUserType: _drop, ...rest } = value;
              onChange(e.target.value ? { ...rest, signUpUserType: e.target.value } : rest);
            }}
          >
            <option value="">{t("access.noSignUp")}</option>
            {signUpIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </NativeSelect>
        )}
      </Field>
      {problem && (
        <p role="alert" className="mb-4 text-sm text-danger" data-testid="access-problem">
          {t(problem)}
        </p>
      )}
    </div>
  );
}
